import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import './AdminDashboard.css';
import './AdminForms.css';

const emptyForm = {
  code: '',
  name: '',
  description: '',
  duration: '',
  basePrice: '',
  morningPrice: '',
  eveningPrice: '',
  isActive: true,
  isFeatured: false,
  sortOrder: 0,
  featuresText: '',
  ctaType: 'booking',
  ctaLink: '',
};

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // db id
  const [form, setForm] = useState(emptyForm);

  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('adminToken')
      : null;

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const loadServices = () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios
      .get(`${API_BASE}/api/admin/services`, {
        headers: authHeaders,
      })
      .then((res) => {
        setServices(res.data.services || []);
      })
      .catch(() => {
        setServices([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const startEdit = (service) => {
    setEditingId(service.dbId);
    setForm({
      code: service.id || '',
      name: service.name || '',
      description: service.description || '',
      duration: service.duration || '',
      basePrice: service.price ?? '',
      morningPrice: service.morningPrice ?? '',
      eveningPrice: service.eveningPrice ?? '',
      isActive: service.active,
      isFeatured: service.featured,
      sortOrder: service.sortOrder || 0,
      featuresText: Array.isArray(service.features)
        ? service.features.join('\n')
        : '',
      ctaType: service.ctaType === 'quote' ? 'quote' : 'booking',
      ctaLink: service.ctaLink || '',
    });
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      // eslint-disable-next-line no-alert
      alert('يرجى تسجيل الدخول مرة أخرى');
      return;
    }

    setSaving(true);
    if (form.ctaType === 'quote' && !form.ctaLink.trim()) {
      // eslint-disable-next-line no-alert
      alert('يرجى إدخال رابط التواصل لزر اطلب تسعيرة');
      setSaving(false);
      return;
    }

    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      duration: form.duration.trim(),
      basePrice: form.basePrice !== '' ? Number(form.basePrice) : null,
      morningPrice:
        form.morningPrice !== '' ? Number(form.morningPrice) : null,
      eveningPrice:
        form.eveningPrice !== '' ? Number(form.eveningPrice) : null,
      isActive: !!form.isActive,
      isFeatured: !!form.isFeatured,
      sortOrder:
        form.sortOrder !== '' ? Number(form.sortOrder) || 0 : 0,
      features: form.featuresText
        .split('\n')
        .map((t) => t.trim())
        .filter(Boolean),
      ctaType: form.ctaType === 'quote' ? 'quote' : 'booking',
      ctaLink: form.ctaType === 'quote' ? form.ctaLink.trim() : '',
    };

    try {
      if (editingId) {
        await axios.put(
          `${API_BASE}/api/admin/services/${editingId}`,
          payload,
          { headers: authHeaders }
        );
      } else {
        await axios.post(`${API_BASE}/api/admin/services`, payload, {
          headers: authHeaders,
        });
      }
      setForm(emptyForm);
      setEditingId(null);
      loadServices();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.response?.data?.error || 'تعذر حفظ الخدمة');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (service) => {
    if (!token) {
      // eslint-disable-next-line no-alert
      alert('يرجى تسجيل الدخول مرة أخرى');
      return;
    }
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `هل أنت متأكد من حذف الخدمة "${service.name}"؟`
    );
    if (!ok) return;

    try {
      await axios.delete(
        `${API_BASE}/api/admin/services/${service.dbId}`,
        { headers: authHeaders }
      );
      if (editingId === service.dbId) {
        setEditingId(null);
        setForm(emptyForm);
      }
      loadServices();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.response?.data?.error || 'تعذر حذف الخدمة');
    }
  };

  return (
    <div className="admin-dashboard">
      <h1 className="admin-page-title">الخدمات</h1>

      <div className="admin-page-content">
        <div className="admin-page-main">
          <div className="admin-list-header">
            <h2>قائمة الخدمات الحالية</h2>
            <button
              type="button"
              className="btn-primary"
              onClick={startCreate}
            >
              خدمة جديدة
            </button>
          </div>

          {loading ? (
            <div className="admin-loading">جاري التحميل...</div>
          ) : services.length === 0 ? (
            <div className="admin-empty">لا توجد خدمات بعد.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>الكود</th>
                    <th>الاسم</th>
                    <th>الأسعار</th>
                    <th>الزر</th>
                    <th>مفعّل؟</th>
                    <th>مميز؟</th>
                    <th>ترتيب</th>
                    <th>التحكم</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.id}>
                      <td>{s.id}</td>
                      <td>{s.name}</td>
                      <td>
                        {s.morningPrice && s.eveningPrice
                          ? `صباحًا ${s.morningPrice} / مساءً ${s.eveningPrice} ريال`
                          : s.morningPrice || s.price
                          ? `${s.morningPrice || s.price} ريال`
                          : '-'}
                      </td>
                      <td>
                        {s.ctaType === 'quote' ? 'اطلب تسعيرة' : 'احجز موعد'}
                      </td>
                      <td>{s.active ? 'نعم' : 'لا'}</td>
                      <td>{s.featured ? 'نعم' : 'لا'}</td>
                      <td>{s.sortOrder ?? 0}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => startEdit(s)}
                        >
                          تعديل
                        </button>
                        <button
                          type="button"
                          className="btn-link danger"
                          onClick={() => handleDelete(s)}
                        >
                          حذف
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="admin-page-side">
          <h2>{editingId ? 'تعديل خدمة' : 'إضافة خدمة جديدة'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>الكود (id)</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => handleChange('code', e.target.value)}
                required
                placeholder="مثال: zoom"
              />
            </div>
            <div className="form-group">
              <label>اسم الخدمة</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>الوصف</label>
              <textarea
                value={form.description}
                onChange={(e) =>
                  handleChange('description', e.target.value)
                }
              />
            </div>
            <div className="form-group">
              <label>المدة</label>
              <input
                type="text"
                value={form.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                placeholder="مثال: 60 دقيقة"
              />
            </div>

            <div className="form-row inline-row">
              <div className="form-group">
                <label>السعر الأساسي</label>
                <input
                  type="number"
                  value={form.basePrice}
                  onChange={(e) =>
                    handleChange('basePrice', e.target.value)
                  }
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>سعر الصباح</label>
                <input
                  type="number"
                  value={form.morningPrice}
                  onChange={(e) =>
                    handleChange('morningPrice', e.target.value)
                  }
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>سعر المساء</label>
                <input
                  type="number"
                  value={form.eveningPrice}
                  onChange={(e) =>
                    handleChange('eveningPrice', e.target.value)
                  }
                  min="0"
                />
              </div>
            </div>

            <div className="form-row inline-row">
              <div className="form-group">
                <label>مفعّل؟</label>
                <select
                  value={form.isActive ? '1' : '0'}
                  onChange={(e) =>
                    handleChange('isActive', e.target.value === '1')
                  }
                >
                  <option value="1">نعم</option>
                  <option value="0">لا</option>
                </select>
              </div>
              <div className="form-group">
                <label>مميز؟</label>
                <select
                  value={form.isFeatured ? '1' : '0'}
                  onChange={(e) =>
                    handleChange('isFeatured', e.target.value === '1')
                  }
                >
                  <option value="1">نعم</option>
                  <option value="0">لا</option>
                </select>
              </div>
              <div className="form-group">
                <label>ترتيب العرض</label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) =>
                    handleChange('sortOrder', e.target.value)
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label>مزايا الخدمة (سطر لكل ميزة)</label>
              <textarea
                value={form.featuresText}
                onChange={(e) =>
                  handleChange('featuresText', e.target.value)
                }
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>نوع الزر</label>
              <select
                value={form.ctaType}
                onChange={(e) => handleChange('ctaType', e.target.value)}
              >
                <option value="booking">احجز موعد</option>
                <option value="quote">اطلب تسعيرة</option>
              </select>
            </div>

            {form.ctaType === 'quote' && (
              <div className="form-group">
                <label>رابط التواصل</label>
                <input
                  type="text"
                  value={form.ctaLink}
                  onChange={(e) => handleChange('ctaLink', e.target.value)}
                  required
                  placeholder="مثال: /contact أو رابط واتساب"
                />
              </div>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving ? 'جارٍ الحفظ...' : 'حفظ'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminServices;

